import React, { useMemo, useEffect, useState } from "react";
import CountriesDropdown, { CONTINENT_COUNTRIES } from "./CountryChoice";
import CityChoice from "./CityChoiceAd";
import type { CitySelection } from "./CityChoiceAd";

type TargetingMode = "continent" | "countries" | "cities";

export type BackendTargeting = {
  // kept for compatibility with your existing backend shape,
  // but this modal will never set it to true.
  isWorldwide?: boolean;
  continents?: string[];
  countries?: string[];
  cities?: string[]; // e.g. "US|CA|Los Angeles" or "HU||Budapest"
};

type Props = {
  open: boolean;
  onClose: () => void;

  initialTargeting: BackendTargeting | null;
  onSave: (next: BackendTargeting) => void;

  // style tokens you already pass
  BORDER: string;
  CARD_BG: string;
};

const CONTINENTS = Object.keys(CONTINENT_COUNTRIES);

const ALL_COUNTRIES = Object.values(CONTINENT_COUNTRIES)
  .flat()
  .filter((c, i, arr) => arr.findIndex((x) => x.code === c.code) === i)
  .sort((a, b) => a.name.localeCompare(b.name));

function parseCityToken(token: string): CitySelection | null {
  const parts = token.split("|");
  if (parts.length < 3) return null;
  const [countryISO, stateRaw, ...cityParts] = parts;
  const city = cityParts.join("|").trim();
  const state = (stateRaw ?? "").trim();
  if (!countryISO || !city) return null;
  return { countryISO: countryISO.trim(), state: state || undefined, city };
}

const LocationChangeAd: React.FC<Props> = ({
  open,
  onClose,
  initialTargeting,
  onSave,
  BORDER,
  CARD_BG,
}) => {
  const [mode, setMode] = useState<TargetingMode>("continent");

  const [selectedContinent, setSelectedContinent] = useState<string>("Europe");
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  const [selectedCityCountryISO, setSelectedCityCountryISO] = useState<string>("");
  const [selectedCityState, setSelectedCityState] = useState<string>("");
  const [selectedCities, setSelectedCities] = useState<CitySelection[]>([]);

  const COUNTRY_CODE_TO_NAME = useMemo(() => {
    return new Map(ALL_COUNTRIES.map((c) => [c.code, c.name]));
  }, []);

  const toggleCountry = (countryISO: string) => {
    setSelectedCountries((prev) =>
      prev.includes(countryISO) ? prev.filter((c) => c !== countryISO) : [...prev, countryISO]
    );
  };

  const toggleCity = (countryISO: string, city: string, state?: string) => {
    setSelectedCities((prev) => {
      const exists = prev.some(
        (x) =>
          x.countryISO === countryISO &&
          x.city === city &&
          (x.state ?? "") === (state ?? "")
      );

      if (exists) {
        return prev.filter(
          (x) =>
            !(
              x.countryISO === countryISO &&
              x.city === city &&
              (x.state ?? "") === (state ?? "")
            )
        );
      }

      return [...prev, { countryISO, city, state }];
    });
  };

  // ✅ Reset button action: clear all choices in the modal
  const reset = () => {
    setMode("continent");
    setSelectedContinent("Europe");

    setSelectedCountries([]);

    setSelectedCityCountryISO("");
    setSelectedCityState("");
    setSelectedCities([]);
  };

  const resetCurrentMode = () => {
    if (mode === "continent") {
      // reset within continent mode (still stays in continent tab)
      setSelectedContinent("Europe"); // or CONTINENTS[0]
      return;
    }
  
    if (mode === "countries") {
      // reset within country mode (still stays in country tab)
      setSelectedCountries([]);
      return;
    }
  
    // mode === "cities"
    setSelectedCities([]);
    setSelectedCityCountryISO("");
    setSelectedCityState("");
  };

  // hydrate state when opening
  useEffect(() => {
    if (!open) return;

    const tgt = initialTargeting || {};

    // Determine mode (NO "world")
    if (tgt.cities?.length) setMode("cities");
    else if (tgt.countries?.length) setMode("countries");
    else setMode("continent");

    // Set values
    setSelectedContinent(tgt.continents?.[0] ?? "Europe");
    setSelectedCountries(tgt.countries ?? []);

    const parsedCities =
      (tgt.cities ?? [])
        .map(parseCityToken)
        .filter((x): x is CitySelection => Boolean(x)) ?? [];

    setSelectedCities(parsedCities);

    // best-effort seed the city dropdowns for nicer UX
    const first = parsedCities[0];
    setSelectedCityCountryISO(first?.countryISO ?? "");
    setSelectedCityState(first?.state ?? "");
  }, [open, initialTargeting]);

  // cleanup/guard when switching modes
  useEffect(() => {
    if (mode !== "countries") setSelectedCountries([]);
  }, [mode]);

  useEffect(() => {
    if (mode !== "cities") {
      setSelectedCityCountryISO("");
      setSelectedCityState("");
      setSelectedCities([]);
    }
  }, [mode]);

  if (!open) return null;

  const chipStyle: React.CSSProperties = {
    padding: "6px 10px",
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: "transparent",
    color: "#e5e7eb",
    fontSize: 12,
    cursor: "pointer",
  };

  const chipActiveStyle: React.CSSProperties = {
    ...chipStyle,
    background: "rgba(250,204,21,0.18)",
  };

  const labelStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 13,
    marginTop: 10,
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: "#020617",
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    padding: "8px 10px",
    color: "#e5e7eb",
    fontSize: 13,
    outline: "none",
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
  };

  const helperTextStyle: React.CSSProperties = {
    fontSize: 12,
    opacity: 0.8,
    marginTop: 6,
  };

  const save = () => {
    const next: BackendTargeting =
      mode === "continent"
        ? { isWorldwide: false, continents: [selectedContinent], countries: [], cities: [] }
        : mode === "countries"
        ? { isWorldwide: false, continents: [], countries: selectedCountries, cities: [] }
        : {
            isWorldwide: false,
            continents: [],
            countries: [],
            cities: selectedCities.map((x) => `${x.countryISO}|${x.state ?? ""}|${x.city}`),
          };

    onSave(next);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(760px, 100%)",
          background: CARD_BG,
          border: `1px solid ${BORDER}`,
          borderRadius: 16,
          padding: 16,
          boxShadow: "0 20px 45px rgba(0,0,0,0.55)",
          color: "#e5e7eb",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Change location targeting</div>

          <button
            type="button"
            onClick={onClose}
            style={{
              borderRadius: 999,
              border: `1px solid ${BORDER}`,
              background: "transparent",
              color: "#e5e7eb",
              fontSize: 12,
              padding: "6px 10px",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>

        {/* mode chips (NO world) */}
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {(["continent", "countries", "cities"] as TargetingMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              style={mode === m ? chipActiveStyle : chipStyle}
            >
              {m === "continent" ? "By continent" : m === "countries" ? "By country" : "By city"}
            </button>
          ))}
        </div>

        {/* selectors */}
        <div style={{ marginTop: 12 }}>
          {mode === "continent" && (
            <div>
              <label style={labelStyle}>
                Continent
                <select
                  value={selectedContinent}
                  onChange={(e) => setSelectedContinent(e.target.value)}
                  style={selectStyle}
                >
                  {CONTINENTS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>

              <div style={helperTextStyle}>
                Selected: <strong>{selectedContinent}</strong>
              </div>
            </div>
          )}

          {mode === "countries" && (
            <div>
              <CountriesDropdown
                label="Countries"
                options={ALL_COUNTRIES}
                value={selectedCountries}
                onToggle={toggleCountry}
                placeholder="None selected"
                labelStyle={labelStyle}
                selectStyle={selectStyle}
              />

              <div style={helperTextStyle}>
                Selected:{" "}
                {selectedCountries.length
                  ? selectedCountries
                      .map((code) => COUNTRY_CODE_TO_NAME.get(code) ?? code)
                      .join(", ")
                  : "None selected"}
              </div>
            </div>
          )}

          {mode === "cities" && (
            <div>
              <CityChoice
                labelCountry="Country"
                labelState="State"
                labelCities="Cities"
                countryOptions={ALL_COUNTRIES}
                selectedCountryISO={selectedCityCountryISO}
                onCountryChange={(iso) => {
                  setSelectedCityCountryISO(iso);
                  setSelectedCityState("");
                }}
                selectedState={selectedCityState}
                onStateChange={(st) => setSelectedCityState(st)}
                selectedCities={selectedCities}
                onToggleCity={toggleCity}
                labelStyle={labelStyle}
                selectStyle={selectStyle}
              />

              <div style={helperTextStyle}>
                Selected:{" "}
                {selectedCities.length
                  ? `${selectedCities.length} city${selectedCities.length === 1 ? "" : "ies"}`
                  : "None selected"}
              </div>
            </div>
          )}
        </div>

        {/* actions */}
     <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
  <button
    type="button"
    onClick={resetCurrentMode}
    style={{
      borderRadius: 999,
      border: `1px solid ${BORDER}`,
      background: "transparent",
      color: "#e5e7eb",
      fontSize: 12,
      padding: "8px 12px",
      cursor: "pointer",
      opacity: 0.9,
    }}
  >
    Reset
  </button>

  <button
    type="button"
    onClick={onClose}
    style={{
      borderRadius: 999,
      border: `1px solid ${BORDER}`,
      background: "transparent",
      color: "#e5e7eb",
      fontSize: 12,
      padding: "8px 12px",
      cursor: "pointer",
    }}
  >
    Cancel
  </button>

  <button
    type="button"
    onClick={save}
    style={{
      borderRadius: 999,
      border: "none",
      background: "linear-gradient(135deg, #eab308, #facc15)",
      color: "#111827",
      fontSize: 12,
      padding: "8px 12px",
      cursor: "pointer",
      fontWeight: 600,
    }}
  >
    Save changes
  </button>
</div>

      </div>
    </div>
  );
};

export default LocationChangeAd;
